import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Plus,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Euro,
  Loader2,
  ChevronRight,
  Wallet,
  AlertTriangle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  estimated: { label: 'Estimation reçue', color: 'bg-blue-100 text-blue-700', icon: Euro },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
  converted: { label: 'Projet créé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const MyProjectRequestsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [hourlyRate, setHourlyRate] = useState(75);
  
  // Modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [balanceError, setBalanceError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, walletRes] = await Promise.all([
        api.get('/project-requests/'),
        api.get('/wallet/').catch(() => ({ data: null }))
      ]);
      
      setRequests(requestsRes.data || []);
      
      if (walletRes.data) {
        setWalletBalance(walletRes.data.balance_hours || 0);
        setHourlyRate(walletRes.data.hourly_rate || 75);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptEstimation = async () => {
    if (!selectedRequest) return;
    
    setActionLoading(true);
    setBalanceError(null);
    
    try {
      const response = await api.post(`/project-requests/${selectedRequest.id}/accept`);
      toast.success('Projet créé avec succès !');
      setShowAcceptModal(false);
      setSelectedRequest(null);
      
      // Navigate to the new project
      if (response.data.project_id) {
        navigate(`/projects/${response.data.project_id}`);
      } else {
        fetchData();
      }
    } catch (error) {
      if (error.response?.data?.detail?.error === 'insufficient_balance') {
        const detail = error.response.data.detail;
        setBalanceError({
          message: detail.message,
          deficit_hours: detail.deficit_hours,
          deficit_amount: detail.deficit_amount,
          current_balance: detail.current_balance,
          required_hours: detail.required_hours
        });
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Voulez-vous vraiment refuser cette estimation ?')) return;
    
    try {
      await api.post(`/project-requests/${requestId}/reject`, { reason: 'Refusé par le client' });
      toast.success('Demande refusée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du refus');
    }
  };

  const openAcceptModal = (request) => {
    setSelectedRequest(request);
    setBalanceError(null);
    setShowAcceptModal(true);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-24">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Mes demandes de projet
              </h1>
              <p className="text-slate-500 mt-1">
                Suivez l'état de vos demandes et validez les estimations
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-cyan-50 rounded-xl">
                <Wallet className="w-5 h-5 text-cyan-600" />
                <div>
                  <p className="text-xs text-slate-500">Mon solde</p>
                  <p className="font-bold text-cyan-700">{walletBalance.toFixed(1)}h</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/new-project-request')}
                className="gap-2"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Plus className="w-4 h-4" />
                Nouvelle demande
              </Button>
            </div>
          </div>

          {/* Requests List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <Card className="card">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Vous n'avez pas encore de demande de projet</p>
                <Button
                  onClick={() => navigate('/new-project-request')}
                  className="gap-2"
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Plus className="w-4 h-4" />
                  Créer ma première demande
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => {
                const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                const totalCost = request.estimated_hours ? request.estimated_hours * (request.hourly_rate || hourlyRate) : 0;
                
                return (
                  <Card key={request.id} className="card hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Main Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg text-slate-800 truncate">
                              {request.title}
                            </h3>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                            {request.description || request.audio_transcript || 'Aucune description'}
                          </p>
                          
                          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                            <span>
                              Créé le {new Date(request.created_at).toLocaleDateString('fr-FR')}
                            </span>
                            {request.inspiration_links?.length > 0 && (
                              <span>{request.inspiration_links.length} lien(s)</span>
                            )}
                            {request.images?.length > 0 && (
                              <span>{request.images.length} image(s)</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Estimation & Actions */}
                        <div className="flex flex-col items-end gap-3">
                          {request.status === 'estimated' && (
                            <div className="text-right">
                              <p className="text-xs text-slate-500">Estimation</p>
                              <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                                {request.estimated_hours}h
                              </p>
                              <p className="text-sm text-slate-600">
                                {totalCost.toLocaleString('fr-FR')}€ HT
                              </p>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            {request.status === 'estimated' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReject(request.id)}
                                  className="text-red-600"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Refuser
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => openAcceptModal(request)}
                                  className="bg-green-600 hover:bg-green-700 text-white gap-1"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Accepter
                                </Button>
                              </>
                            )}
                            
                            {request.status === 'converted' && request.project_id && (
                              <Button
                                size="sm"
                                onClick={() => navigate(`/projects/${request.project_id}`)}
                                className="gap-1"
                                style={{ backgroundColor: theme.colors?.accent }}
                              >
                                Voir le projet
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {request.status === 'pending' && (
                              <Badge variant="outline" className="text-amber-600">
                                <Clock className="w-3 h-3 mr-1" />
                                En cours d'étude
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Estimation Notes */}
                      {request.estimation_notes && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600 font-medium mb-1">Note de l'agence :</p>
                          <p className="text-sm text-blue-800">{request.estimation_notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Accept Estimation Modal */}
      <Dialog open={showAcceptModal} onOpenChange={setShowAcceptModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              Valider l'estimation
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-2">{selectedRequest.title}</p>
                <p className="text-4xl font-bold" style={{ color: theme.colors?.primary }}>
                  {selectedRequest.estimated_hours}h
                </p>
                <p className="text-lg text-slate-600">
                  {(selectedRequest.estimated_hours * (selectedRequest.hourly_rate || hourlyRate)).toLocaleString('fr-FR')}€ HT
                </p>
              </div>
              
              {/* Balance Check */}
              {!balanceError ? (
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Votre solde actuel</span>
                    <span className="font-semibold">{walletBalance.toFixed(1)}h</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Heures requises</span>
                    <span className="font-semibold text-amber-600">-{selectedRequest.estimated_hours}h</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Solde après validation</span>
                    <span className={cn(
                      "font-bold",
                      walletBalance >= selectedRequest.estimated_hours ? "text-green-600" : "text-red-600"
                    )}>
                      {(walletBalance - selectedRequest.estimated_hours).toFixed(1)}h
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-500 shrink-0" />
                    <div>
                      <p className="font-medium text-red-700">{balanceError.message}</p>
                      <div className="mt-3 space-y-1 text-sm">
                        <p>Solde actuel : <strong>{balanceError.current_balance?.toFixed(1)}h</strong></p>
                        <p>Requis : <strong>{balanceError.required_hours}h</strong></p>
                        <p className="text-red-600">
                          Il vous manque : <strong>{balanceError.deficit_hours?.toFixed(1)}h</strong> ({balanceError.deficit_amount?.toFixed(2)}€)
                        </p>
                      </div>
                      <Button
                        className="mt-4 w-full gap-2"
                        onClick={() => navigate('/wallet')}
                        style={{ backgroundColor: theme.colors?.accent }}
                      >
                        <Wallet className="w-4 h-4" />
                        Recharger mon wallet
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedRequest.estimation_notes && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium mb-1">Note de l'agence :</p>
                  <p className="text-sm text-blue-800">{selectedRequest.estimation_notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcceptModal(false)}>
              Annuler
            </Button>
            {!balanceError && (
              <Button 
                onClick={handleAcceptEstimation}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700 text-white gap-1"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Confirmer et démarrer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyProjectRequestsPage;
